import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [needs2FA, setNeeds2FA] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const totpRef = useRef(null);

  useEffect(() => {
    if (needs2FA && totpRef.current) {
      totpRef.current.focus();
    }
  }, [needs2FA]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await login(email, password, needs2FA ? totpCode : undefined);

      if (result.requires_2fa) {
        setNeeds2FA(true);
        setTotpCode('');
        setLoading(false);
        return;
      }

      if (result.requires_2fa_setup) {
        navigate('/security');
      } else {
        navigate(result.role === 'admin' || result.role === 'standard' ? '/admin' : '/');
      }
    } catch (err) {
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else if (err.response) {
        setError(`Server error (${err.response.status})`);
      } else if (err.request) {
        setError('Unable to reach server. Please check your connection.');
      } else {
        setError(err.message || 'Login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setNeeds2FA(false);
    setTotpCode('');
    setError('');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <img
            src={darkMode ? '/logo-white - full.png' : '/Logo in black.png'}
            alt="Startup Accountancy"
            className="h-20 mx-auto mb-4"
          />
          <h1 className="text-3xl font-bold text-brand-700">Payroll Portal</h1>
          <h2 className="mt-2 text-xl text-gray-600 dark:text-gray-400">
            {needs2FA ? 'Two-Factor Authentication' : 'Sign in to your account'}
          </h2>
        </div>
        <form className="mt-8 space-y-6 bg-white dark:bg-gray-800 p-8 rounded-lg shadow" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {!needs2FA ? (
            <div className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email address</label>
                <input
                  id="email" type="email" required value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="you@company.com"
                />
              </div>
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Password</label>
                <input
                  id="password" type="password" required value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="p-3 bg-blue-50 dark:bg-blue-900/30 rounded text-sm text-blue-700 dark:text-blue-300">
                <svg className="w-5 h-5 inline-block mr-1 -mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                Enter the 6-digit code from your authenticator app
              </div>
              <div>
                <label htmlFor="totp" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Authentication Code</label>
                <input
                  ref={totpRef}
                  id="totp"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  required
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-center text-2xl tracking-widest font-mono"
                  placeholder="000000"
                  autoComplete="one-time-code"
                />
              </div>
              <button
                type="button"
                onClick={handleBack}
                className="text-sm text-brand-600 hover:text-brand-500 dark:text-brand-400"
              >
                &larr; Back to login
              </button>
            </div>
          )}

          <button
            type="submit" disabled={loading || (needs2FA && totpCode.length !== 6)}
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 disabled:opacity-50"
          >
            {loading ? 'Signing in...' : needs2FA ? 'Verify' : 'Sign in'}
          </button>

          {!needs2FA && (
            <div className="text-center text-sm space-y-2">
              <div>
                <Link to="/forgot-password" className="font-medium text-brand-600 hover:text-brand-500">Forgot your password?</Link>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400">Don't have an account? </span>
                <Link to="/register" className="font-medium text-brand-600 hover:text-brand-500">Register</Link>
              </div>
            </div>
          )}
        </form>
        <div className="text-center text-xs text-gray-400 dark:text-gray-500 space-y-1">
          <p>First time logging in? You'll need an authenticator app (e.g. Google Authenticator) to set up two-factor authentication.</p>
          <p>
            Need help?{' '}
            <a href="mailto:payroll@startupaccountancy.com" className="text-brand-500 hover:text-brand-600 dark:text-brand-400">
              payroll@startupaccountancy.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
